<template>
  <main class="submit-page" :class="{ 'dark-mode': isDarkMode }">
    <section v-if="!isAuthenticated" class="page-shell auth-shell" aria-labelledby="auth-title">
      <div class="state-surface">
        <i class="bi bi-lock" aria-hidden="true"></i>
        <div>
          <p class="section-kicker">Kirish</p>
          <h1 id="auth-title">Akkauntga kirish kerak</h1>
          <p>Yangi so'z qo'shish uchun avval profilingizga kiring.</p>
        </div>
        <button type="button" class="md-button filled" @click="goToLogin">
          Kirish
        </button>
      </div>
    </section>

    <section v-else class="page-shell" aria-labelledby="submit-title">
      <header class="page-header">
        <div>
          <p class="section-kicker">Yangi yozuv</p>
          <h1 id="submit-title">Yangi tushuncha qo'shish</h1>
          <p>So'z, ta'rif va misolni tekshirib yuboring. Tag tanlash ro'yxatda tartibni saqlaydi.</p>
        </div>
      </header>

      <form class="form-surface" @submit.prevent="submitDefinition" novalidate>
        <div class="field">
          <label for="term">So'z</label>
          <input
            id="term"
            v-model.trim="term"
            class="md-input"
            :class="{ 'is-error': Boolean(termError) }"
            type="text"
            autocomplete="off"
            placeholder="Masalan: qadriyat"
            required
            :aria-invalid="Boolean(termError)"
            aria-describedby="term-support"
          />
          <p id="term-support" class="supporting-text" :class="{ error: Boolean(termError) }" role="status">
            {{ termError || "Qisqa, aniq va takrorsiz so'z kiriting." }}
          </p>
        </div>

        <div class="field">
          <label for="definition">Ma'nosi</label>
          <textarea
            id="definition"
            v-model.trim="definition"
            class="md-input textarea"
            rows="5"
            placeholder="So'zning ma'nosini tushunarli yozing"
            required
          ></textarea>
        </div>

        <div class="field">
          <label for="example">Misol</label>
          <textarea
            id="example"
            v-model.trim="example"
            class="md-input textarea"
            rows="3"
            placeholder="So'z qatnashgan qisqa gap"
          ></textarea>
        </div>

        <div class="field">
          <label for="tag">Tag</label>
          <select
            id="tag"
            v-model="tagName"
            class="md-input"
            :disabled="tagsLoading"
            required
            aria-describedby="tag-support"
          >
            <option value="" disabled>{{ tagsLoading ? 'Taglar yuklanmoqda' : 'Tagni tanlang' }}</option>
            <option v-for="tag in tags" :key="tag.id" :value="tag.id">{{ tag.name }}</option>
          </select>
          <p id="tag-support" class="supporting-text" :class="{ error: Boolean(tagsError) }" role="status">
            {{ tagsError || "Kerakli bo'limni tanlang." }}
          </p>
        </div>

        <p v-if="errorMessage" class="status-message error" role="alert">{{ errorMessage }}</p>
        <p v-if="successMessage" class="status-message success" role="status">{{ successMessage }}</p>

        <div class="form-actions">
          <button type="button" class="md-button tonal" :disabled="isSubmitting" @click="resetForm">
            Tozalash
          </button>
          <button type="submit" class="md-button filled" :disabled="isSubmitting || tagsLoading">
            <span v-if="isSubmitting" class="button-spinner" aria-hidden="true"></span>
            Yuborish
          </button>
        </div>
      </form>
    </section>
  </main>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import axios from 'axios';

const term = ref('');
const definition = ref('');
const example = ref('');
const tagName = ref('');
const tags = ref([]);
const errorMessage = ref('');
const termError = ref('');
const successMessage = ref('');
const isSubmitting = ref(false);
const tagsLoading = ref(false);
const tagsError = ref('');
const redirectTimer = ref(null);

const store = useStore();
const router = useRouter();
const isDarkMode = computed(() => store.getters.isDarkMode);
const token = computed(() => store.getters.token || localStorage.getItem('auth_token') || '');
const isAuthenticated = computed(() => Boolean(token.value));

onMounted(() => {
  if (isAuthenticated.value) {
    fetchTags();
  }
});

onUnmounted(() => {
  if (redirectTimer.value) {
    window.clearTimeout(redirectTimer.value);
  }
});

async function fetchTags() {
  tagsLoading.value = true;
  tagsError.value = '';

  try {
    const response = await axios.get('phrases/tags/', {
      headers: {
        Authorization: `Bearer ${token.value}`,
        'Content-Type': 'application/json',
      },
    });

    if (Array.isArray(response.data?.tags)) {
      tags.value = response.data.tags;
    } else if (Array.isArray(response.data?.results)) {
      tags.value = response.data.results;
    } else if (Array.isArray(response.data)) {
      tags.value = response.data;
    } else {
      tags.value = [];
    }
  } catch (error) {
    tags.value = [];
    tagsError.value = "Taglarni yuklab bo'lmadi.";
  } finally {
    tagsLoading.value = false;
  }
}

async function submitDefinition() {
  if (!isAuthenticated.value) {
    errorMessage.value = "So'zni yuborish uchun tizimga kiring.";
    return;
  }

  isSubmitting.value = true;
  errorMessage.value = '';
  termError.value = '';
  successMessage.value = '';

  try {
    const formData = {
      title: term.value,
      definition: definition.value,
      example: example.value,
      tag_id: tagName.value,
    };

    const response = await axios.post('phrases/phrases/', formData, {
      headers: {
        Authorization: `Bearer ${token.value}`,
        'Content-Type': 'application/json',
      },
    });

    if (response.status === 201) {
      resetForm();
      successMessage.value = "So'z muvaffaqiyatli qo'shildi.";
      redirectTimer.value = window.setTimeout(() => {
        router.push({ name: 'Home' });
      }, 900);
    }
  } catch (error) {
    const titleError = error.response?.data?.title;
    if (Array.isArray(titleError)) {
      termError.value = titleError.join(' ');
    } else if (typeof titleError === 'string') {
      termError.value = titleError;
    } else {
      errorMessage.value = error.response?.data?.detail || 'Xatolik yuz berdi. Qayta urinib ko\'ring.';
    }
  } finally {
    isSubmitting.value = false;
  }
}

function resetForm() {
  term.value = '';
  definition.value = '';
  example.value = '';
  tagName.value = '';
}

function goToLogin() {
  router.push({ name: 'Profile' });
}
</script>

<style scoped>
.submit-page {
  min-height: 100%;
  color: var(--md-sys-color-on-background);
}

.page-shell {
  width: min(760px, 100%);
  margin: 0 auto;
}

.auth-shell {
  display: grid;
  min-height: min(560px, calc(100vh - 220px));
  place-items: center;
}

.page-header {
  margin-bottom: 20px;
}

.page-header h1,
.state-surface h1 {
  margin: 0 0 8px;
  color: var(--md-sys-color-on-surface);
  font: var(--md-sys-typescale-headline-large);
}

.page-header p,
.state-surface p {
  max-width: 620px;
  margin: 0;
  color: var(--md-sys-color-on-surface-variant);
  font: var(--md-sys-typescale-body-medium);
}

.section-kicker {
  margin: 0 0 4px;
  color: var(--md-sys-color-primary);
  font: var(--md-sys-typescale-label-medium);
  text-transform: uppercase;
}

.form-surface,
.state-surface {
  border: 1px solid var(--md-sys-color-outline-variant);
  border-radius: var(--md-sys-shape-corner-large);
  background: var(--md-sys-color-surface-container-low);
  box-shadow: var(--md-sys-elevation-1);
}

.form-surface {
  display: grid;
  gap: 18px;
  padding: clamp(20px, 4vw, 32px);
}

.state-surface {
  display: grid;
  justify-items: center;
  gap: 16px;
  width: min(520px, 100%);
  padding: 32px;
  text-align: center;
}

.state-surface > i {
  display: grid;
  width: 56px;
  height: 56px;
  place-items: center;
  border-radius: 50%;
  color: var(--md-sys-color-on-primary-container);
  background: var(--md-sys-color-primary-container);
  font-size: 1.6rem;
}

.field {
  display: grid;
  gap: 8px;
}

.field label {
  color: var(--md-sys-color-on-surface);
  font: var(--md-sys-typescale-label-large);
}

.md-input {
  width: 100%;
  min-height: 56px;
  padding: 14px 16px;
  border: 1px solid var(--md-sys-color-outline);
  border-radius: var(--md-sys-shape-corner-small);
  color: var(--md-sys-color-on-surface);
  background: var(--md-sys-color-surface-container-lowest);
  transition:
    border-color var(--md-sys-motion-standard),
    box-shadow var(--md-sys-motion-standard),
    background-color var(--md-sys-motion-standard);
}

.textarea {
  resize: vertical;
}

.md-input:focus {
  border-color: var(--md-sys-color-primary);
  box-shadow: var(--md-focus-ring);
}

.md-input.is-error {
  border-color: var(--md-sys-color-error);
}

.supporting-text,
.status-message {
  margin: 0;
  color: var(--md-sys-color-on-surface-variant);
  font: var(--md-sys-typescale-body-small);
}

.supporting-text.error,
.status-message.error {
  color: var(--md-sys-color-error);
}

.status-message.success {
  color: var(--md-sys-color-secondary);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

.md-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 40px;
  padding: 0 24px;
  border: 0;
  border-radius: var(--md-sys-shape-corner-full);
  cursor: pointer;
  font: var(--md-sys-typescale-label-large);
}

.md-button.filled {
  color: var(--md-sys-color-on-primary);
  background: var(--md-sys-color-primary);
}

.md-button.tonal {
  color: var(--md-sys-color-on-secondary-container);
  background: var(--md-sys-color-secondary-container);
}

.md-button:disabled {
  cursor: not-allowed;
  opacity: var(--md-sys-state-disabled-opacity);
}

.button-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: spin 700ms linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 640px) {
  .form-actions,
  .md-button {
    width: 100%;
  }
}
</style>
